// TeamDashboard.jsx - نسخه نهایی نهایی و کامل (مقیاس‌پذیر + defensive کامل)
// ============================================================
// ✅ قابلیت‌های جدید (Telegram-style):
//   • دکمه شناور پرش به آخرین پیام (نمایش فقط وقتی اسکرول به بالا)
//   • Badge تعداد پیام‌های خوانده‌نشده روی دکمه
//   • جداکننده «پیام‌های جدید» قبل از اولین پیام خوانده‌نشده
//   • علامت‌گذاری خودکار پیام‌ها به‌عنوان خوانده‌شده هنگام پرش
// ============================================================

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import {
  useTeam,
  normalizeReactions,
  getReactionCounts,
  getUserReaction,
} from './useTeam';
import { normalizePhone } from '../utils/normalizePhone';
import './TeamDashboard.css';
import { engine } from '../core/books/storage.js';
import UserProfileModal from './UserProfileModal.jsx';
import TeamSettingsModal from './TeamSettingsModal.jsx';
import FindGroupPage from './FindGroupPage';


// ✅ غیرفعال کردن scroll restoration مرورگر — قبل از هر چیز
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ✅ هماهنگ با AvatarSettings و بقیه اپ
const DEFAULT_AVATAR = '/avatars/avatar34.png';

// ✅ فقط URL معتبر قبول کن — ایموجی/رشته‌های غیر-URL → پیش‌فرض
const resolveAvatar = (raw) => {
  if (!raw) return DEFAULT_AVATAR;
  const s = String(raw).trim();
  if (!s) return DEFAULT_AVATAR;
  if (s.startsWith('/') || s.startsWith('http')) return s;
  return DEFAULT_AVATAR;
};

// ✅ استایل مشترک دایره آواتار
const AVATAR_IMG_STYLE = {
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'inline-block',
  verticalAlign: 'middle',
  background: 'rgba(0,0,0,0.06)',
  flexShrink: 0,
};

// ============================================================
// ===== توابع دفاعی =====
// ============================================================
const ensureArray = (val) => (Array.isArray(val) ? val : []);
const ensureObject = (val) => (val && typeof val === 'object' && !Array.isArray(val)) ? val : {};

const safeContent = (content) => {
  if (typeof content === 'string') return content;
  if (content === null || content === undefined) return '';
  try { return JSON.stringify(content); } catch { return '[محتوای نامعتبر]'; }
};

const idsEqual = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
};

// ============================================================
// ===== کامپوننت Toast =====
// ============================================================
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const icons = { success: '✅', error: '❌', warning: '⚠️', info: 'ℹ️' };

  return (
    <div className={`toast toast--${type || 'info'}`}>
      <span className="toast__icon">{icons[type] || 'ℹ️'}</span>
      <span className="toast__content">{message}</span>
      <button type="button" className="toast__close" onClick={onClose}>✕</button>
    </div>
  );
};

// ============================================================
// ===== کامپوننت منوی پیام =====
// ============================================================
const MessageMenu = ({
  message, isOwn, isChannel, canPin, position, onClose,
  onReply, onCopy, onEdit, onDelete, onPin, onReaction,
  onShowViews,
  popularReactions, allReactions,
}) => {
  const menuRef = useRef(null);
  const [showMoreReactions, setShowMoreReactions] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => { onClose(); }, 150);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) handleClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClose]);

  const handleReactionClick = (emoji) => { onReaction(emoji); handleClose(); };

  const safePosition = ensureObject(position);
  const menuStyle = {
    position: 'fixed',
    top: Math.min(safePosition.y || 0, window.innerHeight - 320),
    left: Math.min(safePosition.x || 0, window.innerWidth - 220),
    zIndex: 1000,
    opacity: isClosing ? 0 : 1,
    transform: isClosing ? 'scale(0.92)' : 'scale(1)',
    transition: 'opacity 0.15s ease, transform 0.15s ease',
  };

  const popularArr = ensureArray(popularReactions);
  const allArr = ensureArray(allReactions);

  return (
    <div className="message-menu" ref={menuRef} style={menuStyle}>
      <div className="message-menu__content">
        <div className="message-menu__reactions">
          {popularArr.map((emoji) => (
            <button key={emoji} className="message-menu__reaction-btn" onClick={() => handleReactionClick(emoji)}>
              {emoji}
            </button>
          ))}
          <button
            className="message-menu__reaction-btn message-menu__reaction-btn--more"
            onClick={(e) => { e.stopPropagation(); setShowMoreReactions(!showMoreReactions); }}
          >
            <span className="message-menu__more-icon">⋯</span>
          </button>
        </div>

        {showMoreReactions && (
          <div className="message-menu__reactions-popup">
            {allArr.map((emoji) => (
              <button key={emoji} className="message-menu__reaction-btn" onClick={() => handleReactionClick(emoji)}>
                {emoji}
              </button>
            ))}
            <button className="message-menu__reaction-btn message-menu__reaction-btn--close" onClick={() => setShowMoreReactions(false)}>✕</button>
          </div>
        )}

        <div className="message-menu__divider"></div>

        <button type="button" className="message-menu__item" onClick={() => { onReply(); handleClose(); }}>
          <i className="message-menu__icon fas fa-reply"></i><span>پاسخ</span>
        </button>

        <button type="button" className="message-menu__item" onClick={() => { onCopy(); handleClose(); }}>
          <i className="message-menu__icon fas fa-copy"></i><span>کپی</span>
        </button>

        {isOwn && (
          <button type="button" className="message-menu__item" onClick={() => { onEdit(); handleClose(); }}>
            <i className="message-menu__icon fas fa-edit"></i><span>ویرایش</span>
          </button>
        )}

        {canPin && isChannel && (
          <button type="button" className="message-menu__item" onClick={() => { onPin(); handleClose(); }}>
            <i className="message-menu__icon fas fa-thumbtack"></i>
            <span>{message?.pinned ? 'آنپین' : 'پین'}</span>
          </button>
        )}

        {isOwn && (
          <button className="message-menu__item message-menu__item--danger" onClick={() => { onDelete(); handleClose(); }}>
            <i className="message-menu__icon fas fa-trash-alt"></i><span>حذف</span>
          </button>
        )}

        {isOwn && ensureArray(message?.readBy).length > 0 && (
  <button
    type="button"
    className="message-menu__item message-menu__item--views"
    onClick={() => { onShowViews(); handleClose(); }}
  >
    <i className="message-menu__icon fas fa-eye"></i>
    <span>{ensureArray(message?.readBy).length} بازدید</span>
  </button>
)}


      </div>
    </div>
  );
};

// ============================================================
// ===== کامپوننت نمایش نظرسنجی =====
// ============================================================
const PollDisplay = ({
  poll, messageId, onVote, onClose, onDelete, onPin, onSuggest,
  isTeacherOrAdmin, currentUserId,
}) => {
  const [showVoters, setShowVoters] = useState(false);
  const [now, setNow] = useState(Date.now());

  const safePoll = ensureObject(poll);
  const safeOptions = ensureArray(safePoll.options);
  const totalVotes = safeOptions.reduce((sum, opt) => sum + (Number(opt?.votes) || 0), 0);
  const pollId = messageId;

  // ✅ شمارش معکوس زنده (هر ثانیه)
  useEffect(() => {
    if (!safePoll.expiresAt) return;
    if (safePoll.isClosed) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [safePoll.expiresAt, safePoll.isClosed]);

  // ✅ چک انقضا
  const isExpired = !!(safePoll.expiresAt && now >= safePoll.expiresAt);
  const isLocked = safePoll.isClosed || isExpired;

  // ✅ فرمت زمان باقی‌مونده
  const formatTimeLeft = (ms) => {
    if (ms <= 0) return 'پایان یافت';
    const totalSec = Math.floor(ms / 1000);
    const d = Math.floor(totalSec / 86400);
    const h = Math.floor((totalSec % 86400) / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (d > 0) return `${d} روز و ${h} ساعت`;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const timeLeftMs = safePoll.expiresAt ? safePoll.expiresAt - now : 0;
  const timeLeftStr = safePoll.expiresAt ? formatTimeLeft(timeLeftMs) : null;

  // ✅ برای دانش‌آموز، بعد از پایان فقط یه کارت کوچیک
  if (isExpired && !isTeacherOrAdmin) {
    return (
      <div className="group-chat__poll group-chat__poll--expired-placeholder">
        <span className="group-chat__poll-expired-icon">⏰</span>
        <span className="group-chat__poll-expired-text">
          تایمر نظر سنجی تمام شد
        </span>
      </div>
    );
  }

  const hasVoted = () => {
    const uv = safePoll.userVotes?.[currentUserId];
    if (Array.isArray(uv)) return uv.length > 0;
    return uv !== undefined && uv !== null;
  };

  const handleVote = (optionIndex) => {
    if (isLocked) return;
    if (hasVoted() && !safePoll.allowChangeVote) return;
    onVote(pollId, optionIndex);
  };

  const isOptionSelected = (optionIndex) => {
    const uv = safePoll.userVotes?.[currentUserId];
    if (Array.isArray(uv)) return uv.includes(optionIndex);
    return uv === optionIndex;
  };

  const showResults = isLocked || hasVoted() || !safePoll.hideResults;

  return (
    <div className={`group-chat__poll ${isLocked ? 'group-chat__poll--locked' : ''}`}>

      {/* ✅ تایمر بالای نظرسنجی */}
      {timeLeftStr && !safePoll.isClosed && (
        <div className={`group-chat__poll-timer ${isExpired ? 'expired' : ''}`}>
          {isExpired ? '⏰ تایمر تموم شد' : `⏳ زمان باقی‌مانده: ${timeLeftStr}`}
        </div>
      )}

      <div className="group-chat__poll-header">
        <span className="group-chat__poll-question">{safePoll.question || '—'}</span>
        {safePoll.isClosed && <span className="group-chat__poll-closed">🔒 بسته شده</span>}
        {safePoll.isQuiz && <span className="group-chat__poll-quiz">🧠 آزمون</span>}
        {safePoll.isAnonymous && <span className="group-chat__poll-anonymous">🙈 ناشناس</span>}
      </div>

      {safePoll.description && (
        <div className="group-chat__poll-description">{safePoll.description}</div>
      )}

      <div className="group-chat__poll-options">
        {safeOptions.map((option, idx) => {
          const optObj = ensureObject(option);
          const votes = Number(optObj.votes) || 0;
          const percent = totalVotes > 0 ? Math.round((votes / totalVotes) * 100) : 0;
          const isSelected = isOptionSelected(idx);
          const isCorrect = safePoll.isQuiz && safePoll.correctOption === idx;
          const showCorrect = safePoll.isQuiz && (isLocked || hasVoted());
          const isWrong = showCorrect && isSelected && !isCorrect;

          return (
            <div
              key={idx}
              className={`group-chat__poll-option ${isSelected ? 'selected' : ''} ${showCorrect && isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''} ${isLocked ? 'locked' : ''}`}
              onClick={() => !isLocked && handleVote(idx)}
            >
              <div className="group-chat__poll-option-text">
                <span>{optObj.text || '—'}</span>
                {showCorrect && isCorrect && <span className="group-chat__poll-correct-icon">✅</span>}
                {isWrong && <span className="group-chat__poll-wrong-icon">❌</span>}
              </div>
              {showResults && (
                <>
                  <div className="group-chat__poll-option-bar" style={{ width: `${percent}%` }}></div>
                  <div className="group-chat__poll-option-stats">
                    <span>{percent}%</span>
                    <span>({votes})</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="group-chat__poll-footer">
        <span className="group-chat__poll-total-votes">مجموع آراء: {totalVotes}</span>
        {!safePoll.anonymous && totalVotes > 0 && (
          <button className="group-chat__poll-voters-toggle" onClick={() => setShowVoters(!showVoters)}>
            {showVoters ? '👁️ مخفی کردن رأی‌دهندگان' : '👁️ نمایش رأی‌دهندگان'}
          </button>
        )}
        {safePoll.allowSuggest && !isLocked && (
          <button
            className="group-chat__poll-suggest-btn"
            onClick={() => {
              const newOption = prompt('گزینه جدید را وارد کنید:');
              if (newOption && newOption.trim()) onSuggest(pollId, newOption.trim());
            }}
          >
            ➕ پیشنهاد گزینه
          </button>
        )}
      </div>

      {showVoters && !safePoll.anonymous && (
        <div className="group-chat__poll-voters-list">
          {safeOptions.map((opt, idx) => {
            const optObj = ensureObject(opt);
            const voters = ensureArray(optObj.voterNames);
            return (
              <div key={idx} className="group-chat__poll-voters-group">
                <span className="group-chat__poll-voters-option">{optObj.text || '—'}:</span>
                <span className="group-chat__poll-voters-names">
                  {voters.length > 0 ? voters.join('، ') : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};








const JoinRequestMessage = ({
  message, canRespond, onRespond, onViewProfile, currentUserId, busy,
}) => {
  const [now, setNow] = useState(Date.now());
  const jr = message?.joinRequest;
  const isPending = jr?.status === 'pending';

  useEffect(() => {
    if (!isPending || !jr?.expiresAt) return;
    const int = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(int);
  }, [isPending, jr?.expiresAt]);

  if (!jr) return null;

  const remainingMs = jr.expiresAt ? Math.max(0, jr.expiresAt - now) : 0;
  const minutes = Math.floor(remainingMs / 60000);
  const seconds = Math.floor((remainingMs % 60000) / 1000);
  const timeStr = `${minutes}:${String(seconds).padStart(2, '0')}`;
  const isUrgent = remainingMs > 0 && remainingMs < 60000;

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #f8fafc, #eef2ff)',
        border: '1.5px solid #c7d2fe',
        borderRadius: 14,
        padding: 14,
        margin: '4px 0',
        maxWidth: 400,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img
          src={jr.userAvatar || '/avatars/avatar34.png'}
          alt=""
          style={{
            width: 40, height: 40, borderRadius: '50%',
            objectFit: 'cover', background: '#e2e8f0',
          }}
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = '1';
              e.currentTarget.src = '/avatars/avatar34.png';
            }
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>
            {jr.userName}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 2 }}>
            درخواست عضویت در گروه را دارد
          </div>
        </div>
        {isPending && jr.expiresAt && (
          <span
            style={{
              fontSize: '0.8rem',
              fontWeight: 700,
              color: isUrgent ? '#dc2626' : '#6b7280',
              background: isUrgent ? '#fee2e2' : '#f1f5f9',
              padding: '4px 10px',
              borderRadius: 8,
              fontFamily: 'monospace',
              direction: 'ltr',
            }}
          >
            ⏳ {timeStr}
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={() => onViewProfile?.(jr.userId)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          padding: '8px 12px',
          background: '#ffffff',
          border: '1.5px solid #cbd5e1',
          borderRadius: 10,
          cursor: 'pointer',
          fontSize: '0.85rem',
          color: '#334155',
          fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        👁️ مشاهده پروفایل
      </button>

      {isPending && (
        <>
          {canRespond ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => { if (!busy) onRespond?.(message.id, 'accept'); }}
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: busy ? '#6ee7b7' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? '⋯' : '✓ پذیرش'}
              </button>
              <button
                type="button"
                onClick={() => { if (!busy) onRespond?.(message.id, 'reject'); }}
                disabled={busy}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: busy ? '#fca5a5' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: 10,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: '0.88rem',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? '⋯' : '✕ رد'}
              </button>
            </div>
          ) : (
            <div
              style={{
                padding: '8px 12px',
                background: '#f1f5f9',
                borderRadius: 10,
                fontSize: '0.8rem',
                color: '#64748b',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              فقط مدیر یا نماینده می‌تواند پاسخ دهد
            </div>
          )}
        </>
      )}

      {jr.status === 'accepted' && (
        <div
          style={{
            padding: '10px 12px',
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            borderRadius: 10,
            fontSize: '0.85rem',
            color: '#065f46',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          ✅ پذیرفته شد توسط {jr.respondedByName || 'مدیر'}
        </div>
      )}
      {jr.status === 'rejected' && (
        <div
          style={{
            padding: '10px 12px',
            background: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: 10,
            fontSize: '0.85rem',
            color: '#991b1b',
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          ❌ رد شد توسط {jr.respondedByName || 'مدیر'}
        </div>
      )}
    </div>
  );
};

// ============================================================
// 🆕 کامپوننت پیام سیستمی
// ============================================================
const SystemMessage = ({ message }) => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '8px 16px',
        margin: '4px 0',
      }}
    >
      <span
        style={{
          background: 'rgba(99, 102, 241, 0.1)',
          color: '#4f46e5',
          fontSize: '0.78rem',
          padding: '6px 14px',
          borderRadius: 12,
          fontWeight: 600,
          textAlign: 'center',
          lineHeight: 1.6,
        }}
      >
        {message.content || ''}
      </span>
    </div>
  );
};






// ============================================================
// ===== مودال لیست بازدیدکنندگان (Telegram-style) =====
// ============================================================
const ViewsModal = ({ message, onClose, getUserName, getUserAvatar }) => {
  const readers = ensureArray(message?.readBy);

  return (
    <div className="modal-overlay modal-overlay--views" onClick={onClose}>
      <div className="modal modal--views" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>👁️ دیده شده توسط {readers.length} نفر</h3>
          <button type="button" className="modal__close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal__body modal__body--views">
          {readers.length === 0 ? (
            <p className="views-empty">هنوز کسی این پیام را ندیده است</p>
          ) : (
            <ul className="views-list">
              {readers.map((uid) => (
<li key={String(uid)} className="views-list__item">
  <span className="views-list__avatar">
<img
  src={getUserAvatar(uid)}
  alt=""
  style={{ ...AVATAR_IMG_STYLE, width: '32px', height: '32px' }}
  onError={(e) => {
    if (!e.currentTarget.dataset.fb) {
      e.currentTarget.dataset.fb = '1';
      e.currentTarget.src = DEFAULT_AVATAR;
    }
  }}
/>
  </span>
  <span className="views-list__name">{getUserName(uid)}</span>
</li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal__footer modal__footer--views">
          <button type="button" className="views-back-btn" onClick={onClose}>
            ← بازگشت
          </button>
        </div>
      </div>
    </div>
  );
};



const PinnedListModal = ({
  pinnedMessages,
  onClose,
  onSelectMessage,
  getUserName,
  getUserAvatar,
  formatTime,
}) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--pinned-list" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <h3>📌 پیام‌های پین‌شده ({pinnedMessages.length})</h3>
          <button type="button" className="modal__close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal__body modal__body--pinned-list">
          {pinnedMessages.length === 0 ? (
            <p className="pinned-list-empty">هیچ پیام پین‌شده‌ای نیست</p>
          ) : (
            <ul className="pinned-list">
              {pinnedMessages.map((msg) => (
                <li
                  key={msg.id}
                  className="pinned-list__item"
                  onClick={() => { onSelectMessage(msg.id); onClose(); }}
                >
<div className="pinned-list__header">
  <span className="pinned-list__avatar">
    <img
      src={getUserAvatar(msg.senderId)}
      alt=""
      style={{ ...AVATAR_IMG_STYLE, width: '32px', height: '32px' }}
      onError={(e) => {
        if (!e.currentTarget.dataset.fb) {
          e.currentTarget.dataset.fb = '1';
          e.currentTarget.src = '/avatars/avatar1.png';
        }
      }}
    />
  </span>
  <span className="pinned-list__name">{getUserName(msg.senderId)}</span>
  <span className="pinned-list__time">{formatTime(msg.timestamp)}</span>
</div>
                  <div className="pinned-list__content">
                    {msg.type === 'image' ? '🖼️ تصویر' :
                     msg.type === 'video' ? '🎬 ویدیو' :
                     msg.type === 'poll' ? '📊 نظرسنجی' :
                     safeContent(msg.content)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal__footer">
          <button type="button" className="views-back-btn" onClick={onClose}>← بازگشت</button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// ===== کامپوننت اصلی =====
// ============================================================
const TeamDashboard = () => {
  const navigate = useNavigate();

  // ✅ از AuthContext می‌خونیم — نه از localStorage
  const { user } = useAuth();
  const currentUserId = user?.id;
  const currentUserRole = user?.role;
  const isTeacher = currentUserRole === 'teacher';
  const isStudent = currentUserRole === 'student';

  const {
    team,
    members: rawMembers,
    pendingRequests: rawPendingRequests,
    channelMessages: rawChannelMessages,
    pinnedMessages: rawPinnedMessages,
    presenceMap: rawPresenceMap,
    loading, error, pinnedMessage, unreadCounts, isTyping, typingUser,
    messageReactions, setMessageReactions, refreshTeam, createNewTeam,
    addMember, removeMember, changeMemberRole, updateTeamInfo, deleteTeam,
    respondToInvite, sendChannelMessage, editChannelMessage, deleteChannelMessage,
    pinChannelMessage, sendReaction, loadMoreChannelMessages,
    channelPage, channelTotal, setChannelPage,
    allowStudentMessaging, setAllowStudentMessaging, markMessagesAsRead,
    createPoll, votePoll, closePoll, deletePoll, pinPoll, getPollResults, suggestPollOption,
    // 🆕
    joinMode,
    representativeCanApproveRequests,
    canRespondToJoinRequest,
    respondToJoinRequest,
  } = useTeam(currentUserId, currentUserRole);

  const members = useMemo(() => ensureArray(rawMembers), [rawMembers]);
  const presenceMap = useMemo(() => ensureObject(rawPresenceMap), [rawPresenceMap]);

  const pendingRequests = useMemo(() => ensureArray(rawPendingRequests), [rawPendingRequests]);
  const channelMessages = useMemo(() => ensureArray(rawChannelMessages), [rawChannelMessages]);
  const pinnedMessages = useMemo(() => ensureArray(rawPinnedMessages), [rawPinnedMessages]);

const [isDarkMode, setIsDarkMode] = useState(() => {
  try {
    return localStorage.getItem('navbar2-drawer-dark') === '1';
  } catch { return false; }
});
  const [messageInput, setMessageInput] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showPinnedBanner, setShowPinnedBanner] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [showMemberSidebar, setShowMemberSidebar] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [showPollModal, setShowPollModal] = useState(false);
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [pollType, setPollType] = useState('single');
  const [pollAnonymous, setPollAnonymous] = useState(false);
  const [pollIsQuiz, setPollIsQuiz] = useState(false);
  const [pollCorrectOption, setPollCorrectOption] = useState(0);
  const [pollDuration, setPollDuration] = useState({ value: 0, unit: 'minute' });
  const [pollHideResults, setPollHideResults] = useState(false);
  const [pollShuffleOptions, setPollShuffleOptions] = useState(false);
  const [pollAllowChangeVote, setPollAllowChangeVote] = useState(true);
  const [pollAllowSuggest, setPollAllowSuggest] = useState(false);
  const [pollDescription, setPollDescription] = useState('');



  const [showPinnedListModal, setShowPinnedListModal] = useState(false);
  const [currentPinIndex, setCurrentPinIndex] = useState(0);


  const [viewsMessageId, setViewsMessageId] = useState(null);
const [processingJoinRequest, setProcessingJoinRequest] = useState(null);

  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [replyReturnPos, setReplyReturnPos] = useState(null);

  const [menuState, setMenuState] = useState(null);
  const [swipeState, setSwipeState] = useState(null);

  const [teamName, setTeamName] = useState(team?.name || '');
  const [teamLogo, setTeamLogo] = useState(team?.image || '');
  const [logoOptions] = useState([
    '/Books/riazi-1.jpg', '/Books/farsi-1.jpg', '/Books/motaleat-5.jpg',
    '/Books/olum-6.jpg', '/Books/riazi-4.jpg', '/Books/ghoran-2.jpg',
    '/Books/motaleat-3.jpg', '/Books/hedye-4.jpg', '/Books/motaleat-4.jpg', '/Books/hedye-2.jpg',
  ]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const initialScrollDone = useRef(false);

  const [isNearBottom, setIsNearBottom] = useState(true);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [chatReady, setChatReady] = useState(false);

  // ✅ state‌های جدید برای Telegram-style
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const hasSetFirstUnreadRef = useRef(false);

  const popularReactions = useMemo(() => ['❤️', '👍', '😂'], []);
  const allReactions = useMemo(
    () => ['❤️', '👍', '😂', '😮', '😢', '😡', '🔥', '🎉', '⭐', '👏', '💯', '🤔', '🥰', '😍', '🙌'],
    []
  );

  useEffect(() => {
    if (team) {
      setTeamName(team.name || '');
      setTeamLogo(team.image || '');
    }
  }, [team]);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current || inputRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 180;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...ensureArray(prev), { id, message, type }]);
    setTimeout(() => setToasts((prev) => ensureArray(prev).filter((t) => t.id !== id)), 4500);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => ensureArray(prev).filter((t) => t.id !== id));
  }, []);

  // ✅ منبع حقیقت allowStudentMessaging فقط از team (سرور) میاد
  // localStorage قبلاً duplicate نگه می‌داشت — حالا پاک می‌کنیم
  useEffect(() => {
    try { localStorage.removeItem('groupChatSettings'); } catch {}
  }, []);

// ذخیره در کلید مشترک Navbar2 + اعمال کلاس روی body
useEffect(() => {
  try {
    // 🔧 FIX: استفاده از engine.setRaw تا remoteSync آن را به userPrefs سرور push کند
    engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0');
    document.body.className = isDarkMode ? 'dark-theme' : '';
  } catch {}
  return () => { document.body.className = ''; };
}, [isDarkMode]);

// گوش دادن به تغییرات Navbar2 (storage بین تب‌ها + polling داخل همون تب)
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDarkMode(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      const current = localStorage.getItem('navbar2-drawer-dark') === '1';
      setIsDarkMode((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  // ===== تشخیص پایین بودن کاربر =====
    // ===== تشخیص پایین بودن کاربر (universal — با polling قطعی) =====
  useEffect(() => {
    if (!chatReady) return;

    let lastDistance = -1;

    const check = () => {
      const c = messagesContainerRef.current;
      if (!c) return;

      const { scrollTop, scrollHeight, clientHeight } = c;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // فقط اگه تغییر کرده، state رو آپدیت کن
      if (distanceFromBottom !== lastDistance) {
        lastDistance = distanceFromBottom;
        const isBottom = distanceFromBottom < 100;
        setIsNearBottom(isBottom);
        if (isBottom) setNewMessageCount(0);
      }
    };

    const container = messagesContainerRef.current;
    if (!container) return;

    // event listeners برای واکنش فوری
    container.addEventListener('scroll', check, { passive: true });
    container.addEventListener('touchmove', check, { passive: true });
    container.addEventListener('touchend', check, { passive: true });
    container.addEventListener('wheel', check, { passive: true });

    // polling هر 100ms — backup قطعی
    const interval = setInterval(check, 100);

    // یک بار فوری
    check();

    return () => {
      container.removeEventListener('scroll', check);
      container.removeEventListener('touchmove', check);
      container.removeEventListener('touchend', check);
      container.removeEventListener('wheel', check);
      clearInterval(interval);
    };
  }, [chatReady]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  }, []);

  const scrollToMessage = useCallback((messageId) => {
  const container = messagesContainerRef.current;
  const el = messageRefs.current[messageId];
  if (!container || !el) return;

  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const relativeTop = elRect.top - containerRect.top;
  const desiredTop = 60;

  container.scrollTo({
    top: container.scrollTop + relativeTop - desiredTop,
    behavior: 'smooth',
  });
}, []);





// ============================================================
// ✅ پرش به پیام نقل‌قول + هایلایت + دکمه بازگشت
// ============================================================
const jumpToQuotedMessage = useCallback((messageId) => {
  if (!messageId) return;

  const container = messagesContainerRef.current;
  const el = messageRefs.current[messageId];
  if (!container || !el) {
    console.warn('⚠️ پیام مورد نظر پیدا نشد');
    return;
  }

  // ۱. ذخیره پوزیشن فعلی (برای بازگشت)
  setReplyReturnPos({
    scrollTop: container.scrollTop,
    messageId: messageId,
  });

  // ۲. اسکرول نرم به پیام
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const relativeTop = elRect.top - containerRect.top;
  const desiredTop = container.clientHeight * 0.3; // ۳۰٪ از بالا

  container.scrollTo({
    top: container.scrollTop + relativeTop - desiredTop,
    behavior: 'smooth',
  });

  // ۳. هایلایت موقت
  // ۳. هایلایت موقت (۵ ثانیه)
  setHighlightedMessageId(messageId);
  setTimeout(() => {
    setHighlightedMessageId((prev) => (prev === messageId ? null : prev));
  }, 7000);
}, []);

// ✅ بازگشت به جای قبلی
const returnToPreviousPosition = useCallback(() => {
  if (!replyReturnPos) return;
  const container = messagesContainerRef.current;
  if (!container) return;

  container.scrollTo({
    top: replyReturnPos.scrollTop,
    behavior: 'smooth',
  });

  // پاک‌سازی
  setTimeout(() => {
    setReplyReturnPos(null);
    setHighlightedMessageId(null);
  }, 400);
}, [replyReturnPos]);






// ============================================================
// ✅ لغو خودکار هایلایت + دکمه بازگشت با کلیک روی صفحه
// ============================================================
useEffect(() => {
  if (!replyReturnPos) return;

  const handleAnyClick = (e) => {
    // نذار کلیک روی خود دکمه بازگشت، لغو کنه
    if (e.target.closest('.group-chat__return-btn')) return;
    setReplyReturnPos(null);
    setHighlightedMessageId(null);
  };

  document.addEventListener('mousedown', handleAnyClick);
  document.addEventListener('touchstart', handleAnyClick);

  return () => {
    document.removeEventListener('mousedown', handleAnyClick);
    document.removeEventListener('touchstart', handleAnyClick);
  };
}, [replyReturnPos]);







  const handlePinnedBarClick = useCallback(() => {
  if (pinnedMessages.length === 0) return;
  if (pinnedMessages.length === 1) {
    scrollToMessage(pinnedMessages[0].id);
    return;
  }
  const nextIndex = (currentPinIndex + 1) % pinnedMessages.length;
  setCurrentPinIndex(nextIndex);
  const target = pinnedMessages[nextIndex];
  if (target) scrollToMessage(target.id);
}, [pinnedMessages, currentPinIndex, scrollToMessage]);


useEffect(() => {
  if (currentPinIndex >= pinnedMessages.length) {
    setCurrentPinIndex(0);
  }
}, [pinnedMessages.length, currentPinIndex]);

const prevPinnedCountRef = useRef(0);
useEffect(() => {
  if (pinnedMessages.length > prevPinnedCountRef.current) {
    setShowPinnedBanner(true);
  }
  prevPinnedCountRef.current = pinnedMessages.length;
}, [pinnedMessages.length]);




  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    const prevCount = prevMsgCountRef.current;
    const currCount = channelMessages.length;
    if (!isNearBottom && currCount > prevCount && prevCount > 0) {
      setNewMessageCount((prev) => prev + (currCount - prevCount));
    }
    prevMsgCountRef.current = currCount;
  }, [channelMessages.length, isNearBottom]);

  // ===== پارتیکل‌های پس‌زمینه =====
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width, height;
    const colors = ['#FF6B9D', '#A855F7', '#3B82F6', '#06B6D4', '#10B981', '#FBBF24', '#FB923C', '#EF4444'];

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.size = Math.random() * 5 + 2;
        this.speedX = (Math.random() - 0.5) * 0.6;
        this.speedY = (Math.random() - 0.5) * 0.6;
        this.opacity = Math.random() * 0.25 + 0.05;
        this.color = colors[Math.floor(Math.random() * colors.length)];
      }
      update() {
        this.x += this.speedX; this.y += this.speedY;
        if (this.x < 0 || this.x > width) this.speedX *= -1;
        if (this.y < 0 || this.y > height) this.speedY *= -1;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    const particleCount = Math.min(120, Math.floor(window.innerWidth / 10));
    const particles = [];
    for (let i = 0; i < particleCount; i++) particles.push(new Particle());
    particlesRef.current = particles;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p) => { p.update(); p.draw(); });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            const grad = ctx.createLinearGradient(particles[i].x, particles[i].y, particles[j].x, particles[j].y);
            grad.addColorStop(0, particles[i].color);
            grad.addColorStop(1, particles[j].color);
            ctx.strokeStyle = grad;
            ctx.globalAlpha = 0.06 * (1 - dist / 150);
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.globalAlpha = 1;
          }
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      resizeCanvas();
      particles.forEach((p) => { p.x = Math.random() * width; p.y = Math.random() * height; });
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const canPinMessage = useCallback(() => {
    if (isTeacher) return true;
    const currentMember = members.find((m) => m && m.studentId === currentUserId);
    return currentMember?.role === 'admin' || currentMember?.role === 'moderator';
  }, [isTeacher, members, currentUserId]);

  const canSendMessage = useCallback(() => {
    if (isTeacher) return true;
    const currentMember = members.find((m) => m && m.studentId === currentUserId);
    if (!currentMember) return false;
    if (currentMember.role === 'admin' || currentMember.role === 'moderator') return true;
    return allowStudentMessaging;
  }, [isTeacher, members, currentUserId, allowStudentMessaging]);

  const canCreatePoll = useCallback(() => {
    if (isTeacher) return true;
    const currentMember = members.find((m) => m && m.studentId === currentUserId);
    return currentMember?.role === 'admin' || currentMember?.role === 'moderator';
  }, [isTeacher, members, currentUserId]);

  // ===== توابع ری‌اکشن =====
  const handleDoubleClick = useCallback((msgId) => {
    if (!msgId || !currentUserId) return;
    const msg = channelMessages.find((m) => m.id === msgId);
    if (!msg) return;
    const myReaction = getUserReaction(msg.reactions, currentUserId);
    if (myReaction === '❤️') return;
    sendReaction(msgId, '❤️');
  }, [sendReaction, channelMessages, currentUserId]);

  const handleReaction = useCallback((messageId, emoji) => {
    sendReaction(messageId, emoji);
  }, [sendReaction]);

  const openMessageMenu = useCallback((msg, isChannel, position, isOwn, canPin) => {
    setMenuState({
      message: msg, isChannel,
      position: {
        x: Math.min(position.x, window.innerWidth - 220),
        y: Math.min(position.y, window.innerHeight - 320),
      },
      isOwn, canPin,
    });
  }, []);

  const closeMessageMenu = useCallback(() => { setMenuState(null); }, []);

  const handleMessageClick = useCallback((e, msg, isChannel) => {
    if (e.target.closest('a') || e.target.closest('button') || e.target.closest('.group-chat__reaction')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX || rect.left + rect.width / 2;
    const y = e.clientY || rect.top + rect.height / 2;
    const isOwn = msg.senderId === currentUserId;
    const canPin = canPinMessage();
    if (menuState) { closeMessageMenu(); return; }
    openMessageMenu(msg, isChannel, { x, y }, isOwn, canPin);
  }, [currentUserId, canPinMessage, menuState, closeMessageMenu, openMessageMenu]);

  const handleLongPressStart = useCallback((e, msg, isChannel) => {
    const isTouch = e.type === 'touchstart';
    if (!isTouch && e.button !== 2) return;
    longPressTriggeredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      if (isTouch) e.preventDefault();
      handleMessageClick(e, msg, isChannel);
    }, 500);
  }, [handleMessageClick]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleContextMenu = useCallback((e) => { e.preventDefault(); }, []);

  const handleSwipeStart = useCallback((e, msg) => {
    const touch = e.touches?.[0];
    if (!touch) return;
    setSwipeState({ messageId: msg.id, startX: touch.clientX, offset: 0 });
  }, []);

  const handleSwipeMove = useCallback((e) => {
    if (!swipeState) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    const diff = swipeState.startX - touch.clientX;
    const offset = Math.max(-60, Math.min(0, diff));
    setSwipeState((prev) => (prev ? { ...prev, offset } : null));
  }, [swipeState]);

  const handleSwipeEnd = useCallback(() => {
    if (!swipeState) return;
    if (swipeState.offset < -40) {
      const msg = channelMessages.find((m) => m.id === swipeState.messageId);
      if (msg) {
        setReplyTo(msg);
        inputRef.current?.focus();
        addToast('📩 در حال پاسخ به پیام', 'info');
      }
    }
    setSwipeState(null);
  }, [swipeState, channelMessages, addToast]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!messageInput.trim()) return;
    const content = messageInput.trim();
    try {
      await sendChannelMessage(content, 'text', replyTo?.id);
      setMessageInput('');
      setReplyTo(null);
      setTimeout(() => adjustHeight(), 0);
      if (isNearBottom) setTimeout(scrollToBottom, 100);
    } catch (err) {
      addToast(err.message || '❌ خطا در ارسال پیام', 'error');
    }
  }, [messageInput, replyTo, sendChannelMessage, addToast, isNearBottom, scrollToBottom, adjustHeight]);

  const handleEditMessage = useCallback(async (messageId, newContent) => {
    try {
      await editChannelMessage(messageId, newContent);
      setEditingMessage(null);
      addToast('✏️ پیام ویرایش شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [editChannelMessage, addToast]);

  const handleDeleteMessage = useCallback(async (messageId) => {
    if (!window.confirm('آیا از حذف این پیام مطمئن هستید؟')) return;
    try {
      await deleteChannelMessage(messageId);
      addToast('🗑️ پیام حذف شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [deleteChannelMessage, addToast]);

  const handlePinMessage = useCallback(async (messageId, pin) => {
    if (!canPinMessage()) {
      addToast('❌ شما اجازه پین کردن پیام را ندارید', 'error');
      return;
    }
    try {
      await pinChannelMessage(messageId, pin);
      addToast(pin ? '📌 پیام پین شد' : '📌 پیام آنپین شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [pinChannelMessage, addToast, canPinMessage]);

  const handleCopyMessage = useCallback((text) => {
    const copyText = typeof text === 'string' ? text : safeContent(text);
    navigator.clipboard?.writeText(copyText)?.then(() => {
      addToast('📋 کپی شد', 'success');
    })?.catch(() => {
      const ta = document.createElement('textarea');
      ta.value = copyText;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      addToast('📋 کپی شد', 'success');
    });
  }, [addToast]);

  const handleReply = useCallback((message) => {
    setReplyTo(message);
    inputRef.current?.focus();
  }, []);

  const handleCreatePoll = useCallback(async () => {
    const safeOpts = ensureArray(pollOptions);
    if (!pollQuestion.trim() || safeOpts.some((opt) => !opt.trim())) {
      addToast('لطفاً سؤال و همه‌ی گزینه‌ها را پر کنید', 'error');
      return;
    }
    const config = {
      type: pollType, anonymous: pollAnonymous, isQuiz: pollIsQuiz,
      correctOption: pollIsQuiz ? pollCorrectOption : undefined,
      duration: pollDuration.value > 0 ? pollDuration : undefined,
      hideResults: pollHideResults, shuffleOptions: pollShuffleOptions,
      allowChangeVote: pollAllowChangeVote, allowSuggest: pollAllowSuggest,
      description: pollDescription.trim() || undefined,
    };
    try {
      await createPoll(pollQuestion, safeOpts.filter((opt) => opt.trim()), config);
      addToast('✅ نظرسنجی ایجاد شد', 'success');
      setShowPollModal(false);
      setPollQuestion('');
      setPollOptions(['', '']);
      setPollType('single');
      setPollAnonymous(false);
      setPollIsQuiz(false);
      setPollCorrectOption(0);
      setPollDuration({ value: 0, unit: 'minute' });
      setPollHideResults(false);
      setPollShuffleOptions(false);
      setPollAllowChangeVote(true);
      setPollAllowSuggest(false);
      setPollDescription('');
    } catch (err) { addToast(err.message, 'error'); }
  }, [
    pollQuestion, pollOptions, pollType, pollAnonymous, pollIsQuiz,
    pollCorrectOption, pollDuration, pollHideResults, pollShuffleOptions,
    pollAllowChangeVote, pollAllowSuggest, pollDescription, createPoll, addToast,
  ]);

  const handleVotePoll = useCallback(async (pollId, optionIndex) => {
    try {
      await votePoll(pollId, optionIndex);
      addToast('✅ رأی شما ثبت شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [votePoll, addToast]);

  const handleClosePoll = useCallback(async (pollId) => {
    if (!window.confirm('آیا از بستن این نظرسنجی مطمئن هستید؟')) return;
    try {
      await closePoll(pollId);
      addToast('🔒 نظرسنجی بسته شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [closePoll, addToast]);

  const handleDeletePoll = useCallback(async (pollId) => {
    if (!window.confirm('آیا از حذف این نظرسنجی مطمئن هستید؟')) return;
    try {
      await deletePoll(pollId);
      addToast('🗑️ نظرسنجی حذف شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [deletePoll, addToast]);

  const handlePinPoll = useCallback(async (pollId) => {
    try {
      await pinPoll(pollId);
      addToast('📌 نظرسنجی پین شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [pinPoll, addToast]);

  const handleSuggestPollOption = useCallback(async (pollId, newOption) => {
    try {
      await suggestPollOption(pollId, newOption);
      addToast('✅ گزینه جدید اضافه شد', 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [suggestPollOption, addToast]);

  const handleSearchUser = useCallback(async () => {
    const phone = searchPhone.trim();
    if (!phone) { setSearchError('شماره موبایل را وارد کنید'); return; }
    const normalizedInput = normalizePhone(phone);
    try {
      let userFromServer = null;
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        const res = await fetch(`${API_BASE}/users`, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (res.ok) {
          const raw = await res.json();
          const allUsers = ensureArray(raw);
          userFromServer = allUsers.find((u) => u && normalizePhone(u.phone) === normalizedInput);
        }
      } catch (e) { console.warn('سرور در دسترس نیست'); }

            if (userFromServer) {
        setSearchedUser(userFromServer);
        setSearchError('');
        return;
      }

      // ✅ fallback: از کش سراسری (که در useTeam از سرور لود شده)
      const cachedUsers = (typeof window !== 'undefined' && Array.isArray(window.__allUsersCache))
        ? window.__allUsersCache
        : [];
      const found = cachedUsers.find((u) => u && normalizePhone(u.phone) === normalizedInput);

      if (found) {
        setSearchedUser(found);
        setSearchError('');
      } else {
        setSearchedUser(null);
        setSearchError('❌ کاربری با این شماره موبایل یافت نشد');
      }
    } catch (err) {
      console.error('خطا:', err);
      setSearchError('خطا در جستجوی کاربر');
    }
  }, [searchPhone]);

  const getUserName = useCallback((userId) => {
    if (!userId) return 'کاربر';
    if (idsEqual(userId, team?.teacherId)) return 'معلم';
    const member = members.find((m) => m && idsEqual(m.studentId, userId));
    if (member?.name) return member.name;
    // ✅ fallback: از لیست کاربران (که در useTeam از سرور لود شده) استفاده کن
    if (typeof window !== 'undefined' && window.__allUsersCache) {
      const u = window.__allUsersCache.find((x) => x && idsEqual(x.id, userId));
      if (u) {
        if (u.firstName && u.lastName) return `${u.firstName} ${u.lastName}`.trim();
        if (u.name) return u.name;
        if (u.fullName) return u.fullName;
        if (u.username) return u.username;
        if (u.firstName) return u.firstName;
      }
    }
    return `کاربر ${userId}`;
  }, [members, team]);

const handleAvatarImgError = useCallback((e) => {
  if (!e.currentTarget.dataset.fb) {
    e.currentTarget.dataset.fb = '1';
    e.currentTarget.src = DEFAULT_AVATAR;
  }
}, []);

const getUserAvatar = useCallback((userId) => {
  if (!userId) return DEFAULT_AVATAR;

  // ✅ کاربر فعلی: مستقیم از AuthContext → وقتی خودش آواتار عوض کرد، لحظه‌ای آپدیت
  if (idsEqual(userId, currentUserId)) {
    return resolveAvatar(user?.avatar);
  }

  // ✅ بقیه: از کش اعضا
  const member = members.find((m) => m && idsEqual(m.studentId, userId));
  if (member?.avatar) return resolveAvatar(member.avatar);

  // ✅ از کش سراسری کاربران (که useTeam از سرور لود کرده)
  if (typeof window !== 'undefined' && Array.isArray(window.__allUsersCache)) {
    const u = window.__allUsersCache.find((x) => x && idsEqual(x.id, userId));
    if (u?.avatar) return resolveAvatar(u.avatar);
  }

  // ✅ از localStorage (fallback)
  try {
    const storedUsers = localStorage.getItem('users');
    if (storedUsers) {
      const users = ensureArray(JSON.parse(storedUsers));
      const u = users.find((x) => x && idsEqual(x.id, userId));
      if (u?.avatar) return resolveAvatar(u.avatar);
    }
  } catch {}

  return DEFAULT_AVATAR;
}, [members, currentUserId, user?.avatar, handleAvatarImgError]);

  const handleAddMember = useCallback(async () => {
    if (!searchedUser) return;
    try {
      await addMember(searchedUser.id);
      const fullName = searchedUser.firstName && searchedUser.lastName
        ? `${searchedUser.firstName} ${searchedUser.lastName}`.trim()
        : searchedUser.username || 'کاربر';
      addToast(`✅ درخواست عضویت برای ${fullName} ارسال شد`, 'success');
      setShowAddMemberModal(false);
      setSearchedUser(null);
      setSearchPhone('');
      refreshTeam();
    } catch (err) { addToast(err.message, 'error'); }
  }, [searchedUser, addMember, refreshTeam, addToast]);

  const handleChangeRole = useCallback(async (studentId, role) => {
    try {
      await changeMemberRole(studentId, role);
      addToast(`نقش کاربر به ${role} تغییر یافت`, 'success');
    } catch (err) { addToast(err.message, 'error'); }
  }, [changeMemberRole, addToast]);

  const handleRemoveMember = useCallback(async (studentId) => {
    if (!window.confirm('آیا از حذف این عضو مطمئن هستید؟')) return;
    try {
      await removeMember(studentId);
      addToast('✅ عضو حذف شد', 'success');
      refreshTeam();
    } catch (err) { addToast(err.message, 'error'); }
  }, [removeMember, refreshTeam, addToast]);

  const groupedMessages = useMemo(() => {
    const groups = [];
    const safeMessages = ensureArray(channelMessages);

    const getDateStr = (ts) => {
      try {
        return new Date(ts).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
      } catch { return '—'; }
    };

    safeMessages.forEach((msg, idx) => {
      if (!msg || typeof msg !== 'object') return;
      const date = getDateStr(msg.timestamp);
      const prevDate = idx > 0 ? getDateStr(safeMessages[idx - 1]?.timestamp) : null;
      if (idx === 0 || date !== prevDate) {
        groups.push({ date, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });
    return groups;
  }, [channelMessages]);

  const formatTime = useCallback((iso) => {
    if (!iso) return '';
    try {
      const date = new Date(iso);
      const now = new Date();
      const diff = now - date;
      if (diff < 60000) return 'همین الان';
      if (diff < 3600000) return `${Math.floor(diff / 60000)} دقیقه پیش`;
      if (diff < 86400000) return date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
      return date.toLocaleDateString('fa-IR', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  }, []);

  // ✅ وضعیت آنلاین/آفلاین بر اساس lastSeen
const ONLINE_THRESHOLD_MS = 45000; // ۴۵ ثانیه (کمی بیشتر از heartbeat ۲۰ ثانیه)

const getOnlineStatus = useCallback((userId) => {
  if (!userId) return 'offline';
  const lastSeen = presenceMap[userId] || 0;
  if (!lastSeen) return 'offline';
  const diff = Date.now() - lastSeen;
  return diff < ONLINE_THRESHOLD_MS ? 'online' : 'offline';
}, [presenceMap]);

// ✅ فرمت‌کردن آخرین بازدید به فارسی
const formatLastSeen = useCallback((userId) => {
  const lastSeen = presenceMap[userId] || 0;
  if (!lastSeen) return 'آخرین بازدید نامشخص';

  const diffMs = Date.now() - lastSeen;
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  const month = Math.floor(day / 30);

  if (sec < 60) return 'همین الان آنلاین بود';
  if (min < 60) return `آخرین بازدید ${min} دقیقه پیش`;
  if (hour < 24) return `آخرین بازدید ${hour} ساعت پیش`;
  if (day < 30) return `آخرین بازدید ${day} روز پیش`;
  return `آخرین بازدید ${month} ماه پیش`;
}, [presenceMap]);

  const loadMore = useCallback(async () => {
    const nextPage = (Number(channelPage) || 1) + 1;
    await loadMoreChannelMessages(nextPage);
  }, [channelPage, loadMoreChannelMessages]);

  const hasMoreMessages = channelMessages.length < (Number(channelTotal) || 0);

  // ============================================================
// ✅ علامت‌گذاری پیام‌ها فقط وقتی در viewport دیده می‌شن
// ============================================================
const observerRef = useRef(null);
const pendingReadIdsRef = useRef(new Set());
const readFlushTimerRef = useRef(null);

// Effect 1: ساخت observer وقتی chatReady شد
useEffect(() => {
  if (!chatReady) return;
  if (!currentUserId || !team?.id) return;

  const container = messagesContainerRef.current;
  if (!container) return;

  const flush = () => {
    if (pendingReadIdsRef.current.size === 0) return;
    const ids = Array.from(pendingReadIdsRef.current);
    pendingReadIdsRef.current.clear();
    try { markMessagesAsRead(ids); } catch (e) { /* ignore */ }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      let hasNew = false;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const msgId = el.getAttribute('data-msg-id');
        if (!msgId) return;
        pendingReadIdsRef.current.add(msgId);
        hasNew = true;
      });
      if (hasNew) {
        if (readFlushTimerRef.current) clearTimeout(readFlushTimerRef.current);
        readFlushTimerRef.current = setTimeout(flush, 300);
      }
    },
    {
      root: container,
      rootMargin: '0px',
      threshold: 0.5,
    }
  );

  observerRef.current = observer;

  // observe کردن همه پیام‌های موجود
  container.querySelectorAll('[data-msg-id]').forEach((el) => observer.observe(el));

  return () => {
    observer.disconnect();
    observerRef.current = null;
    if (readFlushTimerRef.current) clearTimeout(readFlushTimerRef.current);
  };
}, [chatReady, currentUserId, team?.id, markMessagesAsRead]);

// Effect 2: observe پیام‌های جدید که میان
useEffect(() => {
  const observer = observerRef.current;
  const container = messagesContainerRef.current;
  if (!observer || !container) return;
  container.querySelectorAll('[data-msg-id]').forEach((el) => observer.observe(el));
}, [channelMessages.length]);

  // ============================================================
  // ✅ تشخیص اولین پیام خوانده‌نشده (فقط یک‌بار در هر بار لود)
  // ============================================================
  useEffect(() => {
    if (hasSetFirstUnreadRef.current) return;
    if (loading) return;
    if (!currentUserId || !team?.id) return;
    if (channelMessages.length === 0) return;

    hasSetFirstUnreadRef.current = true;

    const firstUnread = channelMessages.find(
      (msg) =>
        !idsEqual(msg.senderId, currentUserId) &&
        !ensureArray(msg.readBy).some((id) => idsEqual(id, currentUserId))
    );

    if (firstUnread && firstUnread.id) {
      setFirstUnreadId(firstUnread.id);
    }
  }, [loading, currentUserId, team?.id, channelMessages]);

  // ===== بستن منو با کلیک خارج =====
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuState && !e.target.closest('.message-menu')) closeMessageMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [menuState, closeMessageMenu]);

  // ============================================================
  // ===== اسکرول فوری به پایین (قبل از paint) =====
  // ============================================================
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    hasScrolledRef.current = false;
    hasSetFirstUnreadRef.current = false;
    setFirstUnreadId(null);
    setChatReady(false);
  }, [currentUserId]);

    useLayoutEffect(() => {
    if (loading) return;
    if (hasScrolledRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;
    if (channelMessages.length === 0) return;

    hasScrolledRef.current = true;

    // ✅ پیدا کردن اولین پیام خوانده‌نشده
    const firstUnread = channelMessages.find(
      (msg) =>
        !idsEqual(msg.senderId, currentUserId) &&
        !ensureArray(msg.readBy).some((id) => idsEqual(id, currentUserId))
    );

    // ✅ تابع اسکرول: اگه پیام خوانده‌نشده هست → برو سراغش، وگرنه → پایین
    const doScroll = () => {
      const c = messagesContainerRef.current;
      if (!c) return;

      if (firstUnread && firstUnread.id && messageRefs.current[firstUnread.id]) {
        const el = messageRefs.current[firstUnread.id];
        // فاصله از بالای کانتینر: ۶۰px تا یکمی از قبلش دیده بشه
        const desiredTop = 60;
        const relativeTop = el.offsetTop - c.offsetTop;
        c.scrollTop = Math.max(0, relativeTop - desiredTop);
      } else {
        // fallback: پیام خوانده‌نشده‌ای نیست → برو پایین
        c.scrollTop = c.scrollHeight;
      }
    };

    // اولین اسکرول sync قبل از paint
    doScroll();
    setChatReady(true);

    // چند بار بعد از paint برای اطمینان (تصاویر/فونت‌ها)
    const raf1 = requestAnimationFrame(doScroll);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(doScroll));

    const timers = [
      setTimeout(doScroll, 16),
      setTimeout(doScroll, 50),
      setTimeout(doScroll, 150),
      setTimeout(doScroll, 400),
      setTimeout(doScroll, 800),
    ];

    // ⚠️ روی load و pageshow فقط اگه هیچ پیام خوانده‌نشده‌ای نیست، برو پایین
    // (چون مرورگر ممکنه پوزیشن قبلی رو بازگردونه)
    const onBrowserRestore = () => {
      const c = messagesContainerRef.current;
      if (!c) return;
      if (firstUnread && firstUnread.id && messageRefs.current[firstUnread.id]) {
        const el = messageRefs.current[firstUnread.id];
        const desiredTop = 60;
        const relativeTop = el.offsetTop - c.offsetTop;
        c.scrollTop = Math.max(0, relativeTop - desiredTop);
      } else {
        c.scrollTop = c.scrollHeight;
      }
    };
    window.addEventListener('load', onBrowserRestore);
    window.addEventListener('pageshow', onBrowserRestore);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      timers.forEach(clearTimeout);
      window.removeEventListener('load', onBrowserRestore);
      window.removeEventListener('pageshow', onBrowserRestore);
    };
  }, [loading, channelMessages.length, currentUserId]);

  // ============================================================
  // ===== رفرش خودکار وقتی کاربر به صفحه برمی‌گرده =====
  // ============================================================
  useEffect(() => {
    const onFocus = () => refreshTeam();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshTeam]);

  // 🆕 polling برای دریافت درخواست‌های عضویت جدید (پوشش gap بین FindGroupPage و TeamDashboard)
  const lastJoinCountRef = useRef(-1);
  useEffect(() => {
    if (!team?.id) return;

    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(
          `${API_BASE}/channelMessages?teamId=${encodeURIComponent(team.id)}&type=join_request`
        );
        if (!res.ok) return;
        const data = await res.json();
        const count = Array.isArray(data) ? data.length : 0;

        if (lastJoinCountRef.current >= 0 && count !== lastJoinCountRef.current) {
          try { refreshTeam(); } catch { /* ignore */ }
        }
        lastJoinCountRef.current = count;
      } catch { /* ignore */ }
    };

    tick();
    const interval = setInterval(tick, 10000);
    return () => clearInterval(interval);
  }, [team?.id, refreshTeam]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // ============================================================
  // ✅✅✅ دکمه پرش به آخرین پیام (Telegram-style)
  // ============================================================
  const handleJumpToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
    setNewMessageCount(0);
    // علامت‌گذاری همه پیام‌ها به‌عنوان خوانده‌شده
    if (typeof markMessagesAsRead === 'function') {
      try { markMessagesAsRead(); } catch (e) { /* ignore */ }
    }
  }, [markMessagesAsRead]);

  // ============================================================
  // ===== صفحه‌های راه‌اندازی =====
  // ============================================================
  if (loading) {
    return (
      <div className={`loading ${isDarkMode ? 'loading--dark' : ''}`}>
        <div className="loading__spinner"></div>
        <span className="loading__text">در حال بارگذاری...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`error ${isDarkMode ? 'error--dark' : ''}`}>
        <span className="error__icon">⚠️</span>
        <span className="error__text">خطا: {error}</span>
        <button type="button" className="btn-happy" onClick={refreshTeam} style={{ marginTop: '16px' }}>🔄 تلاش مجدد</button>
      </div>
    );
  }

  if (isTeacher && !team) {
    return (
      <div className={`group-chat ${isDarkMode ? 'group-chat--dark' : ''}`}>
      <div className="setup-page">
        <div className="setup-card">
          <button type="button" className="setup-card__back-btn" onClick={() => navigate(-1)}>← بازگشت</button>
          <h2 className="setup-card__title">🌟 ساخت گروه جدید</h2>
          <form className="setup-card__form" onSubmit={async (e) => {
            e.preventDefault();
            const name = e.target.elements.teamName.value;
            if (!name.trim()) { addToast('لطفاً نام گروه را وارد کنید', 'error'); return; }
            const submitBtn = e.target.querySelector('.setup-card__submit');
            submitBtn.disabled = true;
            submitBtn.innerHTML = '⏳ در حال ساخت...';
            try {
              await createNewTeam(name);
              addToast('✅ گروه ساخته شد', 'success');
            } catch (err) {
              addToast(err.message || 'خطا در ساخت گروه', 'error');
              submitBtn.disabled = false;
              submitBtn.innerHTML = '🚀 بساز!';
            }
          }}>
            <input className="setup-card__input" type="text" name="teamName" placeholder="نام گروه را وارد کنید..." autoFocus />
            <button className="setup-card__submit" type="submit">🚀 بساز!</button>
          </form>
        </div>
      </div>
      </div>
    );
  }

  if (isStudent && !team && pendingRequests.length === 0) {
    return (
      <FindGroupPage
        currentUserId={currentUserId}
        isDarkMode={isDarkMode}
        setIsDarkMode={setIsDarkMode}
        onJoined={() => { refreshTeam(); }}
        onClose={() => navigate('/home')}
      />
    );
  }

  if (isStudent && pendingRequests.length > 0) {
    // 🆕 تفکیک teacher_invite از student_request
    const teacherInvites = pendingRequests.filter(
      (m) => (m.direction || 'teacher_invite') === 'teacher_invite'
    );
    const myPendingRequests = pendingRequests.filter(
      (m) => m.direction === 'student_request'
    );

    // 🆕 حالت ۱: من درخواست داده‌ام و منتظر تأیید
    if (myPendingRequests.length > 0 && teacherInvites.length === 0) {
      const myReq = myPendingRequests[0];
      const teamName = team?.name || myReq?.teamName || 'گروه';
      return (
        <div className={`group-chat ${isDarkMode ? 'group-chat--dark' : ''}`}>
          <div className="setup-page">
            <div className="setup-card setup-card--invite">
              <button type="button" className="setup-card__back-btn" onClick={() => navigate(-1)}>← بازگشت</button>
              <div className="setup-card__icon">⏳</div>
              <h3 className="setup-card__subtitle">درخواست شما در انتظار تأیید</h3>
              <p className="setup-card__desc">
                درخواست عضویت شما در گروه <strong>"{teamName}"</strong> ثبت شده و در انتظار تایید مدیر گروه است.
              </p>
              <div className="setup-card__actions">
                <button
                  type="button"
                  className="setup-card__accept"
                  onClick={() => refreshTeam()}
                >
                  🔄 بررسی مجدد
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 🆕 حالت ۲: معلم دعوتم کرده (رفتار قبلی)
    if (teacherInvites.length > 0) {
      const invite = teacherInvites[0];
      return (
      
      <div className={`group-chat ${isDarkMode ? 'group-chat--dark' : ''}`}>
      <div className="setup-page">
        <div className="setup-card setup-card--invite">
          <button type="button" className="setup-card__back-btn" onClick={() => navigate(-1)}>← بازگشت</button>
          <div className="setup-card__icon">📩</div>
          <h3 className="setup-card__subtitle">دعوت‌نامه!</h3>
          <p className="setup-card__desc">
            معلم از شما خواسته به گروه <strong>"{invite?.teamName || 'گروه'}"</strong> بپیوندید.
          </p>
          <div className="setup-card__actions">
            <button type="button" className="setup-card__accept" onClick={async () => {
              try {
                await respondToInvite(invite.id, 'active');
                await refreshTeam();
                addToast('✅ به گروه پیوستید!', 'success');
              } catch (err) { addToast(err.message, 'error'); }
            }}>✅ بله، می‌پیوندم!</button>
            <button type="button" className="setup-card__reject" onClick={async () => {
              try {
                await respondToInvite(invite.id, 'rejected');
                await refreshTeam();
                addToast('❌ دعوت رد شد', 'warning');
              } catch (err) { addToast(err.message, 'error'); }
            }}>❌ نه، رد می‌کنم</button>
          </div>
        </div>
      </div>
      </div>
    );
  }
  }                    // ← 🆕 این خط را اضافه کن

  // ============================================================
  // ===== صفحه اصلی =====
  // ============================================================
  const showJumpButton = !isNearBottom && channelMessages.length > 0;

  return (
    <div
      className={`group-chat ${isDarkMode ? 'group-chat--dark' : ''}`}
      style={{
        backgroundImage: isDarkMode
          ? `url(${process.env.PUBLIC_URL}/chat-bg.png)`
          : `url(${process.env.PUBLIC_URL}/chat-team.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
      }}
    >
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />

      <div className="toast-container">
        {ensureArray(toasts).map((t) => <Toast key={t.id} {...t} onClose={() => removeToast(t.id)} />)}
      </div>

      <div className="group-chat__bg">
        <div className="group-chat__aurora group-chat__aurora--1"></div>
        <div className="group-chat__aurora group-chat__aurora--2"></div>
        <div className="group-chat__aurora group-chat__aurora--3"></div>
        <div className="group-chat__aurora group-chat__aurora--4"></div>
      </div>

      <div className="group-chat__island-container">
        <header className="group-chat__header">
<div className="group-chat__header-left">
  <div
    className="group-chat__logo"
    onClick={() => setShowSettings(!showSettings)}
    style={{ cursor: 'pointer' }}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowSettings(!showSettings);
      }
    }}
    title="تنظیمات"
    aria-label="باز کردن تنظیمات گروه"
  >
    {team?.image ? <img src={team.image} alt="group" /> : <span>📚</span>}
  </div>
  <div
    className="group-chat__title"
    onClick={() => setShowSettings(!showSettings)}
    style={{ cursor: 'pointer' }}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setShowSettings(!showSettings);
      }
    }}
    title="تنظیمات"
    aria-label="باز کردن تنظیمات گروه"
  >
    <span className="group-chat__name">{team?.name || 'گروه'}</span>
    <span className="group-chat__members-count">
      <span className="dot"></span>
      {members.filter((m) => getOnlineStatus(m.studentId) === 'online').length} آنلاین • {members.length} عضو
      {isTyping && <span className="group-chat__typing-indicator"> • {typingUser || 'کاربر'} در حال تایپ...</span>}
    </span>
  </div>
</div>
          <div className="group-chat__header-right">
            <button
  type="button"
  className="group-chat__settings-btn"
  onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();
  setShowSettings(!showSettings);
}}
  title="تنظیمات"
>
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 00-2 2v.18a2 2 0 01-1 1.73l-.43.25a2 2 0 01-2 0l-.15-.08a2 2 0 00-2.73.73l-.22.38a2 2 0 00.73 2.73l.15.1a2 2 0 011 1.72v.51a2 2 0 01-1 1.74l-.15.09a2 2 0 00-.73 2.73l.22.38a2 2 0 002.73.73l.15-.08a2 2 0 012 0l.43.25a2 2 0 011 1.73V20a2 2 0 002 2h.44a2 2 0 002-2v-.18a2 2 0 011-1.73l.43-.25a2 2 0 012 0l.15.08a2 2 0 002.73-.73l.22-.39a2 2 0 00-.73-2.73l-.15-.08a2 2 0 01-1-1.74v-.5a2 2 0 011-1.74l.15-.09a2 2 0 00.73-2.73l-.22-.38a2 2 0 00-2.73-.73l-.15.08a2 2 0 01-2 0l-.43-.25a2 2 0 01-1-1.73V4a2 2 0 00-2-2z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
            <button
              type="button"
              className="group-chat__home-btn"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/home'); }}
              title="خانه"
            >
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
          </div>
        </header>

        {pinnedMessages.length > 0 && showPinnedBanner && (
  <div className="group-chat__pinned-banner">
    <button
      type="button"
      className="group-chat__pinned-main"
      onClick={handlePinnedBarClick}
      title={pinnedMessages.length > 1 ? 'کلیک برای پین بعدی' : 'رفتن به پیام پین'}
    >
      <span className="group-chat__pinned-icon">📌</span>
      <span className="group-chat__pinned-content">
        {safeContent(pinnedMessages[currentPinIndex]?.content || '')}
      </span>
      {pinnedMessages.length > 1 && (
        <span className="group-chat__pinned-counter">
          {currentPinIndex + 1}/{pinnedMessages.length}
        </span>
      )}
    </button>

    {pinnedMessages.length > 1 && (
      <button
        type="button"
        className="group-chat__pinned-list-btn"
        onClick={() => setShowPinnedListModal(true)}
        title="مشاهده همه پین‌ها"
      >
        ⋮
      </button>
    )}

    {/*{canPinMessage() && pinnedMessages[currentPinIndex] && (
      <button
        type="button"
        className="group-chat__pinned-unpin"
        onClick={() => handlePinMessage(pinnedMessages[currentPinIndex].id, false)}
        title="آنپین"
      >
        ✕
      </button>
    )}*/}

    <button
      type="button"
      className="group-chat__pinned-hide"
      onClick={() => setShowPinnedBanner(false)}
      title="مخفی کردن"
    >
      ✕
    </button>
  </div>
)}

        {/* ✅ کانتینر پیام‌ها: فقط وقتی پیام هست و آماده نیست، مخفی */}
        <div
  className="group-chat__messages-area"
  ref={messagesContainerRef}
  style={{
    ...(!chatReady && channelMessages.length > 0 ? { visibility: 'hidden' } : {}),
  }}
>
          <div className="group-chat__messages">
            {groupedMessages.length === 0 ? (
              <div className="group-chat__empty">
                <span className="group-chat__empty-icon">💬</span>
                <p className="group-chat__empty-text">هیچ پیامی وجود ندارد.</p>
                <p className="group-chat__empty-hint">اولین پیام را شما ارسال کنید!</p>
              </div>
            ) : (
              groupedMessages.map((group, gi) => (
                <React.Fragment key={`g-${gi}`}>
                  <div className="group-chat__date-divider">{group.date}</div>
{ensureArray(group.messages).map((msg) => {
  if (!msg || !msg.id) return null;

  if (msg.type === 'system') {
    return (
      <React.Fragment key={msg.id}>
        {firstUnreadId === msg.id && (
          <div className="group-chat__unread-divider">
            <span className="group-chat__unread-divider-label">پیام‌های جدید</span>
          </div>
        )}
        <SystemMessage message={msg} />
      </React.Fragment>
    );
  }

  if (msg.type === 'join_request') {
    return (
      <React.Fragment key={msg.id}>
        {firstUnreadId === msg.id && (
          <div className="group-chat__unread-divider">
            <span className="group-chat__unread-divider-label">پیام‌های جدید</span>
          </div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: msg.senderId === currentUserId ? 'flex-end' : 'flex-start',
          padding: '6px 12px',
        }}>
          <JoinRequestMessage
            message={msg}
            busy={processingJoinRequest === msg.id}
            canRespond={canRespondToJoinRequest(currentUserId)}
            onRespond={async (messageId, action) => {
              if (processingJoinRequest) return;
              setProcessingJoinRequest(messageId);
              try {
                await respondToJoinRequest(messageId, action);
                addToast?.(
                  action === 'accept' ? '✅ پذیرفته شد' : 'درخواست رد شد',
                  action === 'accept' ? 'success' : 'info'
                );
              } catch (err) {
                addToast?.(err?.message || 'خطا', 'error');
              } finally {
                setProcessingJoinRequest(null);
              }
            }}
            onViewProfile={(uid) => setProfileUserId(uid)}
            currentUserId={currentUserId}
          />
        </div>
      </React.Fragment>
    );
  }

  const isOwn = msg.senderId === currentUserId;

                    const reactionCounts = getReactionCounts(msg.reactions);
                    const myReaction = getUserReaction(msg.reactions, currentUserId);
                    const hasReactions = Object.keys(reactionCounts).length > 0;

                    const isEditing = editingMessage?.id === msg.id;
                    const isPinned = msg.pinned || false;
                    const isReplySwipe = swipeState?.messageId === msg.id;

                    const hasBeenRead = ensureArray(msg.readBy).length > 0;
                    const isPoll = msg.type === 'poll' && msg.pollData;

                    const isFirstUnread = firstUnreadId === msg.id;

                    return (
                      <React.Fragment key={msg.id}>
                        {isFirstUnread && (
                          <div className="group-chat__unread-divider">
                            <span className="group-chat__unread-divider-label">پیام‌های جدید</span>
                          </div>
                        )}
                        <div
                          ref={(el) => { if (el) messageRefs.current[msg.id] = el; }}
                          data-msg-id={msg.id}
                          className={`group-chat__message ${isOwn ? 'group-chat__message--sent' : 'group-chat__message--received'} ${isReplySwipe ? 'group-chat__message--swipe' : ''} ${isPoll ? 'group-chat__message--poll' : ''} ${highlightedMessageId === msg.id ? 'group-chat__message--highlighted' : ''}`}
                          style={isReplySwipe && swipeState ? { transform: `translateX(${swipeState.offset}px)` } : {}}
                          onClick={(e) => handleMessageClick(e, msg, true)}
                          onDoubleClick={() => handleDoubleClick(msg.id)}
                          onTouchStart={(e) => handleLongPressStart(e, msg, true)}
                          onTouchEnd={handleLongPressEnd}
                          onTouchMove={handleLongPressEnd}
                          onContextMenu={handleContextMenu}
                          onMouseDown={(e) => { if (e.button === 2) handleLongPressStart(e, msg, true); }}
                          onMouseUp={handleLongPressEnd}
                          onTouchStartCapture={(e) => handleSwipeStart(e, msg)}
                          onTouchMoveCapture={handleSwipeMove}
                          onTouchEndCapture={handleSwipeEnd}
                        >
{!isOwn && (
  <div
    className="group-chat__message-sender"
    onClick={(e) => {
      e.stopPropagation();
      if (e.detail === 1) setProfileUserId(msg.senderId);
    }}
    onDoubleClick={(e) => e.stopPropagation()}
    onTouchStart={(e) => e.stopPropagation()}
    style={{ cursor: 'pointer' }}
    role="button"
    tabIndex={0}
    title="مشاهده پروفایل"
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setProfileUserId(msg.senderId);
      }
    }}
  >
    <img
      src={getUserAvatar(msg.senderId)}
      alt=""
      style={{ ...AVATAR_IMG_STYLE, width: '24px', height: '24px' }}
      onError={handleAvatarImgError}
    />
  </div>
)}

                          <div className="group-chat__message-body">

{!isOwn && (
  <div className="group-chat__message-name">
    <span>{getUserName(msg.senderId)}</span>
    <span className="group-chat__message-status">
      {getOnlineStatus(msg.senderId) === 'online' && '🟢'}
    </span>
  </div>
)}

                          {msg.replyTo && (() => {
  // پیدا کردن پیام اصلی که این پیام بهش پاسخ داده
  const originalMsg = channelMessages.find((m) => m.id === msg.replyTo);
  if (!originalMsg) {
    // پیام اصلی حذف شده
    return (
      <div className="group-chat__reply-indicator group-chat__reply-indicator--deleted">
        <span>↩️ پیام حذف شده</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="group-chat__reply-indicator group-chat__reply-indicator--clickable"
      onClick={(e) => {
        e.stopPropagation();
        jumpToQuotedMessage(originalMsg.id);
      }}
      title="رفتن به پیام اصلی"
    >
      <span className="group-chat__reply-indicator__line" />
      <div className="group-chat__reply-indicator__body">
        <span className="group-chat__reply-indicator__name">
          {getUserName(originalMsg.senderId)}
        </span>
        <span className="group-chat__reply-indicator__text">
          {originalMsg.type === 'image' ? '🖼️ تصویر' :
           originalMsg.type === 'video' ? '🎬 ویدیو' :
           originalMsg.type === 'poll' ? '📊 نظرسنجی' :
           safeContent(originalMsg.content)}
        </span>
      </div>
    </button>
  );
})()}

                          <div className="group-chat__message-content">
                            {isEditing ? (
                              <div className="group-chat__message-edit">
                                <input
                                  type="text"
                                  defaultValue={msg.content}
                                  className="group-chat__message-edit-input"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleEditMessage(msg.id, e.target.value);
                                    if (e.key === 'Escape') setEditingMessage(null);
                                  }}
                                  id={`edit-${msg.id}`}
                                  autoFocus
                                />
                                <div className="group-chat__message-edit-actions">
                                  <button
                                    type="button"
                                    className="group-chat__message-edit-save"
                                    onClick={() => {
                                      const el = document.getElementById(`edit-${msg.id}`);
                                      if (el) handleEditMessage(msg.id, el.value);
                                    }}
                                  >
                                    💾 ذخیره
                                  </button>
                                  <button type="button" className="group-chat__message-edit-cancel" onClick={() => setEditingMessage(null)}>✕ انصراف</button>
                                </div>
                              </div>
                            ) : (
                              <>
{isPoll ? (
                                  <PollDisplay
                                    poll={msg.pollData}
                                    messageId={msg.id}
                                    onVote={handleVotePoll}
                                    onClose={handleClosePoll}
                                    onDelete={handleDeletePoll}
                                    onPin={handlePinPoll}
                                    onSuggest={handleSuggestPollOption}
                                    isTeacherOrAdmin={canCreatePoll()}
                                    currentUserId={currentUserId}
                                  />
                                ) : msg.type === 'image' ? (
                                  <img src={msg.content} alt="تصویر" className="group-chat__message-image" />
                                ) : msg.type === 'video' ? (
                                  <video src={msg.content} controls className="group-chat__message-video" />
                                ) : (
                                  <span className="group-chat__message-text">{safeContent(msg.content)}</span>
                                )}
                                {isPinned && <span className="group-chat__message-pin-icon">📌</span>}
                                {msg.edited && <span className="group-chat__message-edited">(ویرایش شده)</span>}
                              </>
                            )}
                          </div>

                          <div className="group-chat__message-footer">
                            <span className="group-chat__message-time">{formatTime(msg.timestamp)}</span>
                            {isOwn && (
                              <span className="group-chat__message-status">
                                {hasBeenRead ? (
                                  <span className="status-read" title="خوانده شده">✓✓</span>
                                ) : (
                                  <span className="status-sent" title="ارسال شده">✓</span>
                                )}
                              </span>
                            )}
                          </div>

                          {hasReactions && !isPoll && (
                            <div className="group-chat__message-reactions">
                              {Object.entries(reactionCounts).map(([emoji, count]) => (
                                <span
                                  key={emoji}
                                  className={`group-chat__reaction ${myReaction === emoji ? 'group-chat__reaction--mine' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleReaction(msg.id, emoji); }}
                                  onDoubleClick={(e) => e.stopPropagation()}
                                >
                                  {emoji}
                                  {count > 1 && <span className="group-chat__reaction-count">{count}</span>}
                                </span>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))
            )}
            {isTyping && (
              <div className="group-chat__typing-bubble">
                <span>{typingUser || 'کاربر'} در حال تایپ</span>
                <span className="group-chat__typing-dots">...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {hasMoreMessages && (
            <div className="group-chat__load-more">
              <button type="button" className="group-chat__load-more-btn" onClick={loadMore}>⬆ بارگذاری بیشتر</button>
            </div>
          )}
        </div>



         {replyReturnPos && (
  <button
    type="button"
    className="group-chat__return-btn"
    onClick={returnToPreviousPosition}
    title="بازگشت به جای قبلی"
    aria-label="بازگشت"
  >
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 14l-4-4 4-4" />
      <path d="M5 10h11a4 4 0 010 8h-1" />
    </svg>
    <span className="group-chat__return-btn__text">بازگشت</span>
  </button>
)}



        

        {/* ✅✅✅ دکمه شناور پرش به آخرین پیام (Telegram-style) */}
        {showJumpButton && (
           
          <button
            type="button"
            className="group-chat__jump-to-bottom"
            onClick={handleJumpToBottom}
            title="پرش به آخرین پیام"
            aria-label="پرش به آخرین پیام"
          >
            {newMessageCount > 0 && (
              <span className="group-chat__jump-to-bottom__badge">
                {newMessageCount > 99 ? '99+' : newMessageCount}
              </span>
            )}
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M19 12l-7 7-7-7" />
            </svg>
          </button>
        )}

        <form className="group-chat__input-form" onSubmit={handleSendMessage}>
          {replyTo && (
            <div className="group-chat__reply-preview">
              <span>↩️ در پاسخ به: {safeContent(replyTo.content)}</span>
              <button type="button" className="group-chat__reply-cancel" onClick={() => setReplyTo(null)}>✕</button>
            </div>
          )}
          <div className="group-chat__input-group">
            <textarea
              ref={textareaRef}
              rows="1"
              className="group-chat__input"
              placeholder={canSendMessage() ? 'پیام خود را بنویسید...' : '🔒 ارسال پیام برای دانش آموزان غیر فعال است'}
              value={messageInput}
              onChange={(e) => { setMessageInput(e.target.value); adjustHeight(); }}
              onInput={adjustHeight}
              disabled={!canSendMessage()}
              style={{
                resize: 'none',
                overflow: 'hidden',
                maxHeight: '180px',
                minHeight: '46px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
                padding: '10px 16px',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'var(--gc-text-primary)',
                width: '100%',
                flex: 1,
              }}
            />
            {canCreatePoll() && (
              <button type="button" className="group-chat__poll-btn" onClick={() => setShowPollModal(true)}>🏆</button>
            )}
            <button type="submit" className="group-chat__send-btn" disabled={!canSendMessage()}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </form>
      </div>

      {/* ===== منوی پیام ===== */}
      {menuState && (
        <MessageMenu
          message={menuState.message}
          isOwn={menuState.isOwn}
          isChannel={menuState.isChannel}
          canPin={menuState.canPin}
          position={menuState.position}
          onClose={closeMessageMenu}
          onShowViews={() => setViewsMessageId(menuState.message.id)}
          onReply={() => handleReply(menuState.message)}
          onCopy={() => handleCopyMessage(menuState.message.content)}
          onEdit={() => { if (menuState.isOwn) { setEditingMessage(menuState.message); closeMessageMenu(); } }}
          onDelete={() => { if (menuState.isOwn) { handleDeleteMessage(menuState.message.id); closeMessageMenu(); } }}
          onPin={() => {
            const pin = !menuState.message.pinned;
            handlePinMessage(menuState.message.id, pin);
            closeMessageMenu();
          }}
          onReaction={(emoji) => handleReaction(menuState.message.id, emoji)}
          popularReactions={popularReactions}
          allReactions={allReactions}
        />
      )}



      {viewsMessageId && (() => {
  const msg = channelMessages.find((m) => m.id === viewsMessageId);
  if (!msg) return null;
  return (
    <ViewsModal
      message={msg}
      onClose={() => setViewsMessageId(null)}
      getUserName={getUserName}
      getUserAvatar={getUserAvatar}
    />
  );
})()}


{showPinnedListModal && (
  <PinnedListModal
    pinnedMessages={pinnedMessages}
    onClose={() => setShowPinnedListModal(false)}
    onSelectMessage={(msgId) => scrollToMessage(msgId)}
    getUserName={getUserName}
    getUserAvatar={getUserAvatar}
    formatTime={formatTime}
  />
)}


      {/* ===== مودال تنظیمات (جدید) ===== */}
      {showSettings && (
        <TeamSettingsModal
          team={team}
          members={members}
          presenceMap={presenceMap}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          updateTeamInfo={updateTeamInfo}
          deleteTeam={deleteTeam}
          changeMemberRole={changeMemberRole}
          removeMember={removeMember}
          refreshTeam={refreshTeam}
          onClose={() => setShowSettings(false)}
          onOpenAddMember={() => setShowAddMemberModal(true)}
          onViewProfile={(uid) => setProfileUserId(uid)}
          navigate={navigate}
          getUserName={getUserName}
          getUserAvatar={getUserAvatar}
          getOnlineStatus={getOnlineStatus}
          formatLastSeen={formatLastSeen}
          addToast={addToast}
          logoOptions={logoOptions}
        />
      )}

      {/* ===== مودال جستجو ===== */}
      {showSearchModal && (
        <div className="modal-overlay" onClick={() => setShowSearchModal(false)}>
          <div className="modal modal--search" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h3>🔍 جستجو</h3>
              <button type="button" className="modal__close-btn" onClick={() => setShowSearchModal(false)}>✕</button>
            </div>
            <div className="modal__body">
              <input
                type="text"
                className="modal__input modal__input--large"
                placeholder="نام یا شماره موبایل را وارد کنید..."
                value={searchPhone}
                onChange={(e) => setSearchPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
                autoFocus
              />
              <button type="button" className="modal__search-btn" onClick={handleSearchUser}>🔍 جستجو</button>
              {searchError && <p className="modal__error">{searchError}</p>}
              {searchedUser && (
                <div className="modal__preview">
                  <span className="modal__preview-avatar">
  <img
    src={resolveAvatar(searchedUser.avatar)}
    alt=""
    onError={handleAvatarImgError}
  />
</span>
                  <div>
                    <div className="modal__preview-name">
                      {searchedUser.firstName && searchedUser.lastName
                        ? `${searchedUser.firstName} ${searchedUser.lastName}`.trim()
                        : searchedUser.username || 'کاربر'}
                    </div>
                    <div className="modal__preview-phone">📱 {searchedUser.phone || 'شماره ثبت نشده'}</div>
                    <div className="modal__preview-role">
                      {searchedUser.role === 'teacher' ? '👨‍🏫 معلم' : '🧑‍🎓 دانش‌آموز'}
                    </div>
                  </div>
                  <button type="button" className="modal__preview-add" onClick={handleAddMember}>➕ افزودن</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== سایدبار اعضا ===== */}
      {showMemberSidebar && (
        <div className="group-chat__members-sidebar">
          <div className="group-chat__members-header">
            <h3>👥 اعضای گروه</h3>
            <button type="button" className="group-chat__members-close" onClick={() => setShowMemberSidebar(false)}>✕</button>
          </div>
          <div className="group-chat__members-search">
            <input type="text" placeholder="جستجوی عضو..." className="group-chat__members-search-input" onChange={(e) => {}} />
          </div>
          <div className="group-chat__members-list">
            {members.map((member) => {
              if (!member || !member.id) return null;
              const isOnline = getOnlineStatus(member.studentId) === 'online';
              const displayName = getUserName(member.studentId);
              const avatar = getUserAvatar(member.studentId);
              return (
<div key={member.id} className="group-chat__member-item">
<span
  className="group-chat__member-avatar"
  onClick={(e) => {
    e.stopPropagation();
    if (e.detail === 1) setProfileUserId(member.studentId);
  }}
  style={{ cursor: 'pointer' }}
  role="button"
  tabIndex={0}
  title="مشاهده پروفایل"
  onKeyDown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setProfileUserId(member.studentId);
    }
  }}
>
  <img
    src={avatar}
    alt=""
    style={{ ...AVATAR_IMG_STYLE, width: '100%', height: '100%' }}
    onError={handleAvatarImgError}
  />
</span>
  <span className="group-chat__member-name">
    
    {displayName}
    {member.studentId === team?.teacherId && ' 👨‍🏫'}
    {member.role === 'admin' && ' ⭐'}
    {member.role === 'moderator' && ' 🛡️'}
  </span>
  <span className={`group-chat__member-status ${isOnline ? 'online' : 'offline'}`}>
    {isOnline ? '🟢 آنلاین' : `⚪ ${formatLastSeen(member.studentId)}`}
  </span>
                  {(isTeacher || members.find((m) => m.studentId === currentUserId)?.role === 'admin') && member.studentId !== currentUserId && (
                    <div className="group-chat__member-actions">
                      <select
                        className="group-chat__member-role"
                        value={member.role || 'member'}
                        onChange={(e) => handleChangeRole(member.studentId, e.target.value)}
                      >
                        <option value="member">عضو</option>
                        <option value="admin">نماینده</option>
                      </select>
                      <button type="button" className="group-chat__member-remove" onClick={() => handleRemoveMember(member.studentId)}>🗑️</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {(isTeacher || members.find((m) => m.studentId === currentUserId)?.role === 'admin') && (
            <div className="group-chat__members-footer">
              <button
                type="button"
                className="group-chat__members-add"
                onClick={() => { setShowMemberSidebar(false); setShowAddMemberModal(true); }}
              >➕ افزودن عضو</button>
            </div>
          )}
        </div>
      )}

      {/* ===== مودال افزودن عضو ===== */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>➕ افزودن عضو جدید</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
              شماره موبایل کاربر را وارد کنید (مثال: 09123456789)
            </p>
            <input
              type="tel"
              className="modal__input"
              placeholder="شماره موبایل را وارد کنید..."
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearchUser()}
              dir="ltr"
            />
            <button type="button" className="modal__search" onClick={handleSearchUser}>🔍 جستجو</button>
            {searchError && <p className="modal__error">{searchError}</p>}
            {searchedUser && (
              <div className="modal__preview">
                <span className="modal__preview-avatar">
  <img
    src={resolveAvatar(searchedUser.avatar)}
    alt=""
    onError={handleAvatarImgError}
  />
</span>
                <div>
                  <div className="modal__preview-name">
                    {searchedUser.firstName && searchedUser.lastName
                      ? `${searchedUser.firstName} ${searchedUser.lastName}`.trim()
                      : searchedUser.username || 'کاربر'}
                  </div>
                  <div className="modal__preview-phone">📱 {searchedUser.phone || 'شماره ثبت نشده'}</div>
                  <div className="modal__preview-role">
                    {searchedUser.role === 'teacher' ? '👨‍🏫 معلم' : '🧑‍🎓 دانش‌آموز'}
                  </div>
                </div>
                <button type="button" className="modal__preview-add" onClick={handleAddMember}>➕ افزودن</button>
              </div>
            )}
            <button type="button" className="modal__close" onClick={() => setShowAddMemberModal(false)}>✕ بستن</button>
          </div>
        </div>
      )}

      {/* ===== مودال نظرسنجی ===== */}
      {showPollModal && (
        <div className="modal-overlay" onClick={() => setShowPollModal(false)}>
          <div className="modal modal--poll" onClick={(e) => e.stopPropagation()}>
            <h3 className="mohem-1">🏆 نظرسنجی جدید</h3>

            <input
              type="text"
              className="modal__input"
              placeholder="سؤال نظرسنجی..."
              value={pollQuestion}
              onChange={(e) => setPollQuestion(e.target.value)}
              maxLength={255}
            />

            <div className="modal__poll-options">
              <label> گزینه‌ها (حداقل ۲، حداکثر ۱۲)</label>
              {ensureArray(pollOptions).map((opt, idx) => (
                <div key={idx} className="modal__poll-option-row">
                  <input
                    type="text"
                    className="modal__input"
                    placeholder={`گزینه ${idx + 1}`}
                    value={opt}
                    onChange={(e) => {
                      const newOpts = [...pollOptions];
                      newOpts[idx] = e.target.value;
                      setPollOptions(newOpts);
                    }}
                  />
                  {pollOptions.length > 2 && (
                    <button
                      type="button"
                      className="modal__poll-remove-option"
                      onClick={() => {
                        const newOpts = pollOptions.filter((_, i) => i !== idx);
                        setPollOptions(newOpts);
                        if (pollCorrectOption >= newOpts.length) setPollCorrectOption(0);
                      }}
                    >✕</button>
                  )}
                </div>
              ))}
              {pollOptions.length < 12 && (
                <button
                  type="button"
                  className="modal__poll-add-option"
                  onClick={() => setPollOptions([...pollOptions, ''])}
                >➕ افزودن گزینه</button>
              )}
            </div>

            <div className="modal__poll-config">
              <label>
                <input
                  type="checkbox"
                  checked={pollAnonymous}
                  onChange={(e) => setPollAnonymous(e.target.checked)}
                />
                ناشناس
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={pollIsQuiz}
                  onChange={(e) => {
                    setPollIsQuiz(e.target.checked);
                    if (e.target.checked) setPollType('single');
                  }}
                />
                حالت آزمون
              </label>

              {pollIsQuiz && (
                <div className="modal__poll-correct-option">
                  <label>گزینه صحیح:</label>
                  <select value={pollCorrectOption} onChange={(e) => setPollCorrectOption(Number(e.target.value))}>
                    {pollOptions.map((opt, idx) => (
                      <option key={idx} value={idx} disabled={!opt.trim()}>
                        {opt.trim() || `گزینه ${idx + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="modal__poll-duration">
                <label>مدت زمان:</label>
                <input
                  type="number"
                  min="0"
                  value={pollDuration.value}
                  onChange={(e) => setPollDuration({ ...pollDuration, value: Number(e.target.value) })}
                />
                <select
  value={pollDuration.unit}
  onChange={(e) => setPollDuration({ ...pollDuration, unit: e.target.value })}
>
  <option value="second">ثانیه</option>
  <option value="minute">دقیقه</option>
  <option value="hour">ساعت</option>
  <option value="day">روز</option>
</select>
              </div>

              <label>
                <input
                  type="checkbox"
                  checked={pollHideResults}
                  onChange={(e) => setPollHideResults(e.target.checked)}
                />
                مخفی کردن نتایج تا پایان
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={pollShuffleOptions}
                  onChange={(e) => setPollShuffleOptions(e.target.checked)}
                />
                ترتیب تصادفی گزینه‌ها
              </label>

              <label>
                <input
                  type="checkbox"
                  checked={pollAllowChangeVote}
                  onChange={(e) => setPollAllowChangeVote(e.target.checked)}
                />
                اجازه تغییر رأی
              </label>
            </div>

            <textarea
              className="modal__textarea"
              placeholder="توضیحات (اختیاری)"
              value={pollDescription}
              onChange={(e) => setPollDescription(e.target.value)}
              rows="2"
            />

            <div className="modal__actions">
              <button type="button" className="modal__submit" onClick={handleCreatePoll}>📊 ایجاد نظرسنجی</button>
              <button type="button" className="modal__close-btn" onClick={() => setShowPollModal(false)}>✕ انصراف</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== مودال پروفایل ===== */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>👤 پروفایل کاربر</h3>
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '64px', marginBottom: '8px' }}>👤</div>
              <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{user?.firstName || 'کاربر'}</div>
              <div style={{ color: 'var(--gc-text-muted)', fontSize: '0.85rem' }}>{user?.phone || 'شماره ثبت نشده'}</div>
              <div style={{ color: 'var(--gc-text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>
                {user?.role === 'teacher' ? '👨‍🏫 معلم' : '🧑‍🎓 دانش‌آموز'}
              </div>
            </div>
            <button type="button" className="modal__close" onClick={() => setShowProfileModal(false)}>✕ بستن</button>
          </div>
        </div>
      )}









      {/* ===== مودال پروفایل ===== */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>👤 پروفایل کاربر</h3>
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ fontSize: '64px', marginBottom: '8px' }}>👤</div>
              <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>{user?.firstName || 'کاربر'}</div>
              <div style={{ color: 'var(--gc-text-muted)', fontSize: '0.85rem' }}>{user?.phone || 'شماره ثبت نشده'}</div>
              <div style={{ color: 'var(--gc-text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>
                {user?.role === 'teacher' ? '👨‍🏫 معلم' : '🧑‍🎓 دانش‌آموز'}
              </div>
            </div>
            <button type="button" className="modal__close" onClick={() => setShowProfileModal(false)}>✕ بستن</button>
          </div>
        </div>
      )}

      {/* ✅ مودال پروفایل کاربر (جدید) */}
      {profileUserId && (
        <UserProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
          presenceMap={presenceMap}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};





export default TeamDashboard;